import EditorJs, { OutputData } from '@editorjs/editorjs';
import { KeyboardEvent as ReactKeyboardEvent } from 'react';

import EditorJsParser, { EditorJsParserParams } from './EditorJsParser';
import hotKeyConverter from '../../utility/keyCodeConverter';
import { Block, EditorData } from './types';
import { delay } from '../../utility/helpers';

/* eslint-disable no-param-reassign, no-prototype-builtins, operator-linebreak, @typescript-eslint/no-explicit-any, no-case-declarations */


export const parseReadOnly = (data: OutputData): OutputData => {
  try {
    data.blocks.map((block: Block) => {
      if (block.data.text !== undefined) {
        block.data.text = block.data.text
          .replace(/(\[.*?\])|(contenteditable="true")|(contenteditable="false")/gi, '');
      }
      if (block.data.items !== undefined) {
        block.data.items.map((text) => text
          .replace(/(\[.*?\])|(contenteditable="true")|(contenteditable="false")/gi, ''));
      }
      return block;
    });

    return data;
  } catch (e) {
    return data;
  }
};

interface SlateData {
  type?: string;
  text?: string;
  children?: SlateData[];
}

const deserializeFromSlateData = (value: SlateData[]): string[] => {
  // eslint-disable-next-line
  let buffer = new Array<string>(0);

  // eslint-disable-next-line
  const getText = (value:SlateData) => {
    if (value.hasOwnProperty('text')) {
      // Split by line feed
      const linesParsed = (value as any).text.split('\n');
      linesParsed.forEach((nextLine:string) => {
        if (nextLine.trim().length > 0) {
          buffer.push(nextLine);
        }
      });
    }
    if (value.children) {
      value.children.forEach((item) => getText(item));
    }
  };

  value.forEach((item) => getText(item));

  return buffer;
};


export const parseJsonToEditorState = (value: string): OutputData => {
  try {
    const parsedJson = JSON.parse(value);
    // check slate data
    // eslint-disable-next-line
    if (parsedJson[0] && parsedJson[0].hasOwnProperty('children')) {
      const textLines = deserializeFromSlateData(parsedJson);
      const outputData:OutputData = {
        blocks: [],
      };
      textLines.forEach((nextLine:string) => {
        outputData.blocks.push(
          {
            type: 'paragraph',
            data: {
              text: nextLine,
            },
          },
        );
      });
      return outputData;
    }
    return parsedJson;
  } catch (e) {
    return {
      blocks: [
        {
          type: 'paragraph',
          data: {
            text: '',
          },
        },
      ],
    };
  }
};

export const parseJsonToEditorStateWithInstruction = (value: string): EditorData => {
  const parsedData = parseJsonToEditorState(value) as any;
  if (!Array.isArray(parsedData.instructions)) {
    parsedData.instructions = [];
  }

  return parsedData;
};

export const convertEditorStateToHTML = (data: OutputData, params?: EditorJsParserParams): string => {
  const editorParser = new EditorJsParser(data, params);
  return editorParser.parse();
};

export const onPasteEditorHandler = (
  editor: EditorJs | undefined,
  refEditor: HTMLDivElement | null,
  isUnFormated = true,
) => (e: ClipboardEvent) => {
  let text = '';
  const isPrevent = isUnFormated || navigator.platform.indexOf('Mac') > -1;

  if (editor === undefined) {
    return;
  }

  if (isPrevent) {
    e.preventDefault();
    e.stopPropagation();

    if (e.clipboardData) {
      text = e.clipboardData.getData('text/plain');

      const arrayText = text.split('\n').filter((item) => item.trim() !== '');
      let currentBlockIndex = editor.blocks.getCurrentBlockIndex();
      let currentBlock = editor.blocks.getBlockByIndex(currentBlockIndex);

      if (arrayText.length > 1) {
        if (currentBlock.isEmpty) {
          const content = arrayText.shift();
          if (content) {
            const paragraph = currentBlock.holder.querySelector('.ce-paragraph');
            if (paragraph) {
              paragraph.innerHTML = content;
            }
          }
          arrayText.forEach((item) => {
            editor.blocks.insert(
              'paragraph',
              { text: item },
            );
          });
          currentBlockIndex = editor.blocks.getCurrentBlockIndex();
          editor.caret.setToBlock(currentBlockIndex, 'end');
          currentBlock = editor.blocks.getBlockByIndex(currentBlockIndex);

          currentBlock.holder.scrollIntoView({
            inline: 'end',
            block: 'center',
          });
        } else {
          window.document.execCommand('insertText', false, arrayText.join(' '));
        }
      } else {
        window.document.execCommand('insertText', false, arrayText[0] || '');
      }
    }
  }
};

export const setMobileMenuPosition = async (block: HTMLElement) => {
  if (window.innerWidth < 650) {
    if (window.scriptEditorRef) {
      const menuElement = window.scriptEditorRef.querySelector('.ce-toolbar');
      if (menuElement) {
        const toolbox = window.scriptEditorRef.querySelector('.ce-toolbox');
        const toolbar = window.scriptEditorRef.querySelector('.ce-toolbar__plus');
        const mappedMenu = menuElement as HTMLElement;
        const blockHeight = block.offsetHeight;
        const contentOffset = Math.floor(blockHeight / 2);
        const toolbarY = block.offsetTop;

        if (toolbox) {
          const mappedToolbox = toolbox as HTMLElement;
          mappedToolbox.style.transform = `translate3d(0, calc(${contentOffset}px - 50%), 0)`;
        }
        if (toolbar) {
          const mappedToolbar = toolbar as HTMLElement;
          mappedToolbar.style.transform = `translate3d(0, calc(${contentOffset}px - 50%), 0)`;
        }
        mappedMenu.style.transform = `translate3d(0, ${Math.floor(toolbarY)}px, 1px)`;
      }
    }
  }
};

export const onKeyEditorHandler = (editor: EditorJs | undefined) => (e: ReactKeyboardEvent<HTMLDivElement>) => {
  if (editor === undefined) {
    return;
  }

  switch (e.keyCode) {
    case hotKeyConverter('enter'):
      const currentBlockIndex = editor.blocks.getCurrentBlockIndex();

      const currentBlock = editor.blocks.getBlockByIndex(currentBlockIndex);
      if (currentBlockIndex === -1 || !currentBlock) {
        return;
      }
      currentBlock.holder.scrollIntoView({
        inline: 'end',
        block: 'center',
      });
      setMobileMenuPosition(currentBlock.holder);
      break;

    case hotKeyConverter('tab'):
      const _currentBlock = editor.blocks.getBlockByIndex(editor.blocks.getCurrentBlockIndex());
      setMobileMenuPosition(_currentBlock.holder);
      break;

    default:
      break;
  }
};

export const checkEmptyBlockText = (block: Block): boolean => {
  let val = block.data.text?.trim();
  val = val?.replace('&nbsp;', '');
  return !val;
};

export const removeEmptyBlock = (blocks: Block[], fromStart: boolean = true): Block[] => {
  if (blocks && blocks.length) {
    if (fromStart) {
      if (checkEmptyBlockText(blocks[0])) {
        blocks.shift();
        return removeEmptyBlock(blocks);
      }
    } else if (checkEmptyBlockText(blocks[blocks.length - 1])) {
      blocks.pop();
      return removeEmptyBlock(blocks);
    }
  }

  return blocks;
};

export const filterSideBlocks = (blocks: Block[]): Block[] => {
  const result = removeEmptyBlock([...blocks]);
  return removeEmptyBlock(result, false);
};

export const editorMenuObserver = async (editor:EditorJs) => {
  if (window.scriptEditorRef) {
    const menuElement = window.scriptEditorRef.querySelector('.ce-toolbar');
    if (menuElement) {
      const mutationObserver = new MutationObserver(
        async (mutations) => {
          if (window.innerWidth < 650) {
            if (window.scriptEditorRef) {
              let currentFocusedBlock = window.scriptEditorRef.querySelector('.ce-block--focused');
              if (!currentFocusedBlock) {
                const currentBlockIndex = editor.blocks.getCurrentBlockIndex();
                const currentBlock = editor.blocks.getBlockByIndex(currentBlockIndex);
                currentFocusedBlock = currentBlock.holder;
              }
              const mappedElement = currentFocusedBlock as HTMLElement;
              const mappedMenu = menuElement as HTMLElement;
              const toolbarY = mappedElement.offsetTop;

              mappedMenu.style.transform = `translate3d(0, ${Math.floor(toolbarY) - 1}px, 1px)`;
            }
          }
        },
      );

      mutationObserver.observe(menuElement, { attributes: true, attributeFilter: ['style'] });
    }
  }
};


export const addInstructionsToData = (data: string, instructions: any[]) => {
  try {
    const editorData = parseJsonToEditorStateWithInstruction(data);
    editorData.instructions = [...editorData.instructions, ...instructions];

    return JSON.stringify(editorData);
  } catch (e) {
    console.error(e);
    return data;
  }
};

export const getCaretPosition = (element: HTMLElement):Range | null => {
  const selection = window.getSelection();
  if (selection && selection.rangeCount > 0) {
    const currentRange = selection.getRangeAt(0);
    const preCaretRange = currentRange.cloneRange();
    preCaretRange.selectNodeContents(element);
    preCaretRange.setEnd(currentRange.endContainer, currentRange.endOffset);

    return preCaretRange;
  }

  return null;
};

// @ts-ignore
const createRange = (node: HTMLElement | Node, position: number, range?: Range) => {
  try {
    if (!range) {
      // eslint-disable-next-line no-param-reassign
      range = document.createRange();
      range.selectNode(node);
      range.setStart(node, 0);
    }

    if (position === 0) {
      range.setEnd(node, position);
    } else if (node && position > 0) {
      if (node.nodeType === Node.TEXT_NODE) {
        if (node.textContent && node.textContent.length < position) {
          position -= node.textContent.length;
        } else {
          range.setEnd(node, position);
          position = 0;
        }
      } else {
        for (let i = 0; i < node.childNodes.length; i++) {
          range = createRange(node.childNodes[i], position, range);

          if (position === 0) {
            break;
          }
        }
      }
    }

    return range;
  } catch (e) {
    console.error(e);
  }
};

export const setCaretPosition = async (element: HTMLElement, position: number) => {
  await delay(100);
  const selection = window.getSelection();
  if (selection) {
    const range = createRange(element, position);
    if (range) {
      range.collapse(false);
      selection.removeAllRanges();
      selection.addRange(range);
    }
  }
};
